import { currencyMinorDigits, type Currency, type Money } from "../shared/types";

const DECIMAL_PATTERN = /^-?\d+(?:\.\d+)?$/;

export function decimalToMinor(value: string | number, currency: Currency = "CNY"): number {
  const raw = String(value).trim().replace(/,/g, "");
  if (!DECIMAL_PATTERN.test(raw)) {
    throw new Error(`无效金额: ${value}`);
  }

  const negative = raw.startsWith("-");
  const unsigned = negative ? raw.slice(1) : raw;
  const [integer, fraction = ""] = unsigned.split(".");
  const digits = currencyMinorDigits[currency];
  const factor = 10 ** digits;
  const rounded = `${fraction}${"0".repeat(digits + 1)}`.slice(0, digits + 1);
  let minor = Number(integer) * factor + Number(rounded.slice(0, digits) || "0");
  if (Number(rounded[digits]) >= 5) minor += 1;
  if (!Number.isSafeInteger(minor)) throw new Error("金额超出安全整数范围");
  return negative ? -minor : minor;
}

export function decimalToMinorExact(value: string | number, currency: Currency = "CNY"): number {
  const raw = String(value).trim().replace(/,/g, "");
  if (!DECIMAL_PATTERN.test(raw)) {
    throw new Error(`无效金额: ${value}`);
  }
  const fraction = raw.replace(/^-/, "").split(".")[1] ?? "";
  const digits = currencyMinorDigits[currency];
  if (fraction.length > digits) {
    throw new Error(`${currency} 金额最多保留 ${digits} 位小数`);
  }
  return decimalToMinor(raw, currency);
}

export function minorToDecimal(amountMinor: number, currency: Currency = "CNY"): string {
  if (!Number.isSafeInteger(amountMinor)) throw new Error("金额必须为安全整数");
  const sign = amountMinor < 0 ? "-" : "";
  const absolute = Math.abs(amountMinor);
  const digits = currencyMinorDigits[currency];
  const factor = 10 ** digits;
  const fraction = digits ? `.${String(absolute % factor).padStart(digits, "0")}` : "";
  return `${sign}${Math.floor(absolute / factor)}${fraction}`;
}

export function minorToMajor(amountMinor: number, currency: Currency): number {
  if (!Number.isSafeInteger(amountMinor)) throw new Error("金额必须为安全整数");
  return amountMinor / (10 ** currencyMinorDigits[currency]);
}

export function addMoney(values: Money[]): Money {
  if (values.length === 0) throw new Error("至少需要一笔金额");
  const currency = values[0].currency;
  if (values.some((value) => value.currency !== currency)) throw new Error("不能直接相加不同币种");
  return { amountMinor: values.reduce((sum, value) => sum + value.amountMinor, 0), currency };
}

export function convertToCny(amount: Money, rateScaled: number): number {
  if (!Number.isSafeInteger(rateScaled) || rateScaled <= 0) throw new Error("汇率必须为正整数");
  if (amount.currency === "CNY") return amount.amountMinor;
  const sourceFactor = 10 ** currencyMinorDigits[amount.currency];
  return roundBigInt(BigInt(amount.amountMinor) * BigInt(rateScaled) * 100n, BigInt(1_000_000 * sourceFactor));
}

export function money(amountMinor: number, currency: Currency): Money {
  if (!Number.isSafeInteger(amountMinor)) throw new Error("金额必须为安全整数");
  return { amountMinor, currency };
}

function roundBigInt(numerator: bigint, denominator: bigint): number {
  const sign = numerator < 0n ? -1n : 1n;
  const absolute = numerator < 0n ? -numerator : numerator;
  const rounded = sign * ((absolute + denominator / 2n) / denominator);
  const result = Number(rounded);
  if (!Number.isSafeInteger(result)) throw new Error("折算金额超出安全整数范围");
  return result;
}
